import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardHeader, CardBody, Spinner, Alert } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
  coral: '#ff7f50',
  tangerine: '#ff8c42',
  peach: '#ffab73',
};

const DetectionsByAgent = ({ data }) => {
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("🔍 DetectionsByAgent received data:", data);
    
    if (!data) {
      console.log("⚠️ No data provided");
      return;
    }

    if (data.length === 0) {
      console.log("⚠️ Empty data array");
      setSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    console.log(`📊 Processing ${data.length} agents`);

    // Sort by total detections (highest to lowest)
    const sorted = [...data].sort(
      (a, b) =>
        (b.critical + b.high + b.medium + b.low) -
        (a.critical + a.high + a.medium + a.low)
    );

    console.log("📊 Sorted agents:", sorted);

    // Normalizing
    const normalize = (agent) => {
      const total = agent.critical + agent.high + agent.medium + agent.low;
      if (total === 0) return { critical: 0, high: 0, medium: 0, low: 0 };

      return {
        critical: Number(((agent.critical / total) * 100).toFixed(2)),
        high: Number(((agent.high / total) * 100).toFixed(2)),
        medium: Number(((agent.medium / total) * 100).toFixed(2)),
        low: Number(((agent.low / total) * 100).toFixed(2)),
      };
    };

    const normalized = sorted.map(normalize);
    const agentNames = sorted.map((i) => i.agent);

    console.log("📊 Agent names:", agentNames);
    console.log("📊 Normalized data:", normalized);

    setCategories(agentNames);

    // Format data for horizontal bar chart with categories
    setSeries([
      { 
        name: "Critical (%)", 
        data: normalized.map((i, idx) => ({
          x: agentNames[idx],
          y: i.critical
        }))
      },
      { 
        name: "High (%)", 
        data: normalized.map((i, idx) => ({
          x: agentNames[idx],
          y: i.high
        }))
      },
      { 
        name: "Medium (%)", 
        data: normalized.map((i, idx) => ({
          x: agentNames[idx],
          y: i.medium
        }))
      },
      { 
        name: "Low (%)", 
        data: normalized.map((i, idx) => ({
          x: agentNames[idx],
          y: i.low
        }))
      },
    ]);

    setLoading(false);
  }, [data]);

  const options = {
    chart: { 
      type: "bar", 
      stacked: true, 
      toolbar: { show: true },
      fontFamily: 'Poppins, sans-serif'
    },

    plotOptions: {
      bar: { 
        horizontal: true, // HORIZONTAL for better agent name visibility
        barHeight: "75%",
        borderRadius: 4
      }
    },

    dataLabels: {
      enabled: false,
    },

    xaxis: { 
      title: { 
        text: "Percentage of Detections (%)",
        style: {
          color: '#858d98',
          fontSize: '12px',
          fontWeight: 500
        }
      },
      labels: {
        style: {
          colors: '#858d98',
          fontSize: '11px'
        },
        formatter: function(val) {
          return val ? val.toFixed(0) + "%" : "0%";
        }
      },
      min: 0,
      max: 100
    },

    yaxis: {
      labels: {
        style: {
          colors: '#858d98',
          fontSize: '10px',
          fontWeight: 400
        },
        maxWidth: 180,
        trim: false
      }
    },

    colors: ["#dc2626", ORANGE_COLORS.darker, "#f59e0b", ORANGE_COLORS.light],
    
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 500,
      markers: {
        width: 12,
        height: 12,
        radius: 3
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },

    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function(val) {
          return val ? val.toFixed(2) + "%" : "0%";
        }
      }
    },

    grid: {
      borderColor: 'rgba(133, 141, 152, 0.1)',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      }
    }
  };

  return (
    <Card 
      className="h-100"
      style={{ 
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}
    >
      <CardHeader className="d-flex align-items-center justify-content-between" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <h4 className="card-title mb-0 flex-grow-1">
          <span style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
            background: toRgba(ORANGE_COLORS.primary, 0.10),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
            flexShrink: 0
          }}>
            <FeatherIcon icon="bar-chart-2" size={18} style={{ color: ORANGE_COLORS.primary, lineHeight: 1 }} />
          </span>
          Detections by Agent
        </h4>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner style={{ color: ORANGE_COLORS.primary }} />
            <p className="mt-2 text-muted small">Loading chart data...</p>
          </div>
        ) : series.length === 0 || categories.length === 0 ? (
          <Alert 
            style={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              color: '#856404',
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}
          >
            <FeatherIcon icon="info" size={16} className="me-2" />
            No agent detection data available
          </Alert>
        ) : (
          <>
            <ReactApexChart 
              type="bar" 
              height={Math.max(400, categories.length * 40)} 
              options={options} 
              series={series} 
            />
            <div className="mt-3 pt-3 border-top text-center">
              <span className="text-muted small">
                Displaying <strong style={{ color: ORANGE_COLORS.primary }}>{categories.length}</strong> agent{categories.length !== 1 ? 's' : ''} with detections
              </span>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default DetectionsByAgent;
